import React, { useEffect, useMemo, useState } from "react";

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/Modal";

import RHFTextArea from "@/components/form/RHFTextArea";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import { X } from "lucide-react";

import { ButtonShadcn as Button } from "@/components/ui/Button";

import {
    deleteUserReasonDefaultValues,
    deleteUserReasonSchema,
} from "../validation.schema";

import { UserService } from "@/services/userService";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";

const DeleteUserModal = ({ open, onClose, userId, loading = false, setSilentFetch }) => {
    // Local submit loading state (separate from external loading)
    const [submitting, setSubmitting] = useState(false);

    // React Hook Form setup with validation schema
    const methods = useForm({
        resolver: zodResolver(deleteUserReasonSchema),
        mode: "onChange",
        defaultValues: deleteUserReasonDefaultValues,
    });

    const { handleSubmit, reset } = methods;

    // Reset form when modal opens
    useEffect(() => {
        if (!open) return;

        reset(deleteUserReasonDefaultValues);
        setSubmitting(false);
    }, [open, reset]);

    // Prevent delete when required conditions are not met
    const canDelete = useMemo(() => {
        return !loading && !submitting && !!userId;
    }, [loading, submitting, userId]);

    // Combined loading state for UI
    const effectiveLoading = useMemo(
        () => loading || submitting,
        [loading, submitting]
    );

    // Handle delete API call
    const submitHandler = async (formData) => {
        if (!userId) return;
        if (!canDelete) return;

        setSubmitting(true);

        try {
            await UserService.deleteUser(
                userId,
                formData.reason.trim()
            );

            toast.success("User deleted");

            setSubmitting(false);
            onClose?.();
            setSilentFetch(true);
        } catch (e) {
            console.error(e);
            toast.error(apiErrorMessageFormat(e, `Failed to delete user`));

            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(nextOpen) => {
                // Close modal when dialog requests close
                if (!nextOpen) onClose?.();
            }}
        >
            <DialogContent className="w-[520px] md:w-full flex flex-col p-0">

                {/* Header */}
                <DialogHeader className="relative px-5 py-3 border-b border-border-primary shrink-0">
                    <div>
                        <DialogTitle className="text-[15px] font-semibold text-text-primary">
                            Delete User
                        </DialogTitle>

                        <DialogDescription className="text-[12px] text-text-primary/70 mt-0.5">
                            This action cannot be undone. Provide a reason before deleting.
                        </DialogDescription>
                    </div>

                    {/* Close button */}
                    <DialogClose className="cursor-pointer absolute right-5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center">
                        <X size={16} strokeWidth={3} />
                    </DialogClose>
                </DialogHeader>

                {/* Form */}
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(submitHandler)}>
                        <div className="px-5 py-4" style={{ zoom: 0.85 }}>

                            {/* Reason input */}
                            <RHFTextArea
                                name="reason"
                                label="Reason"
                                placeholder="Enter reason for deletion..."
                                required
                                rows={5}
                            />

                            {/* Actions */}
                            <div className="flex justify-end gap-3 mt-6">

                                {/* Cancel */}
                                <DialogClose render={

                                    <Button
                                        variant="outline"
                                        className="border-none"
                                        type="button"
                                        disabled={effectiveLoading}
                                    >
                                        Cancel
                                    </Button>
                                } />

                                {/* Delete */}
                                <Button
                                    className="bg-destructive text-white"
                                    type="submit"
                                    disabled={!canDelete}
                                >
                                    {effectiveLoading
                                        ? "Deleting…"
                                        : "Delete User"}
                                </Button>
                            </div>

                        </div>
                    </form>
                </FormProvider>
            </DialogContent>
        </Dialog>
    );
};

export default DeleteUserModal;